import { authActions } from '@web/app/features/auth'
import type { AuthType } from '@web/types'
import { fetchBaseQuery } from '@reduxjs/toolkit/query/react'
import moment from 'moment'

import { RootState } from '..'
import { appApi } from './appApi'

export type ApiConfig = {
  baseUrl: string
  demoUrl: string
  authTokenPath: string
  debug?: boolean
  timeout?: number
  apiName?: string
}
type ExtraOptionsApi = {
  unauthenticated?: boolean
}

const minutesToRefresh = 5 //5 minutos antes
// Variable para controlar el refresh único
let refreshPromise: Promise<{ data?: Partial<AuthType>; error?: unknown }> | null = null

export function createBaseApi(config: ApiConfig) {
  const { demoUrl, baseUrl, authTokenPath, timeout = 60000, apiName = 'API', debug = true } = config

  const wrappedBaseQuery = (url) => fetchBaseQuery({
    baseUrl: url,
    timeout,
    prepareHeaders(headers, api) {
      const { getState, extraOptions = { unauthenticated: false } } = api
      const { unauthenticated } = extraOptions as ExtraOptionsApi
      if (!unauthenticated) {
        const state = getState() as RootState
        const token = state.auth.authData[authTokenPath]
        if (token) {
          headers.set('Authorization', `Bearer ${token}`)
        }
      }
      return headers
    },
  })

  // BaseQuery base con configuración personalizada
  const rawBaseQuery = async (args, api, extraoptions) => {
    const state = api.getState()
    const isDemo = state.auth.isDemo
    const url = isDemo ? demoUrl : baseUrl
    return wrappedBaseQuery(url)(args, api, extraoptions)
  }

  // Función helper para identificar endpoints de autenticación
  const isAuthEndpoint = (args: Parameters<typeof rawBaseQuery>[0]): boolean => {
    const url = typeof args === 'string' ? args : (args.url as string)
    return url?.includes('/api/auth/token') || url?.includes('/api/auth/refresh') || false
  }

  const checkTokenExpiration = (authData: Partial<AuthType>) => {
    const { exp, refreshToken_expires, refresh_token, external_id } = authData

    // console.log(`${external_id}-[PRE-EXPIRATION]`, authData)
    if (!refresh_token || !exp || !refreshToken_expires) {
      return { action: 'none' as const }
    }

    const now = moment().unix()
    const accessTokenExp = exp
    const refreshTokenExp = moment(refreshToken_expires).unix()
    const timeToAccessExpiry = accessTokenExp - now
    const minutesUntilExpiry = timeToAccessExpiry / 60

    // Si el refresh token ya expiró
    if (now >= refreshTokenExp) {
      return {
        action: 'logout' as const,
        reason: 'Refresh token expirado',
      }
    }

    // Si el access token ya expiró pero el refresh token sigue válido
    if (now >= accessTokenExp) {
      return {
        action: 'refresh' as const,
        reason: 'Access token expirado',
      }
    }

    // Si faltan minutesToRefresh o menos para que expire el access token
    if (minutesUntilExpiry <= minutesToRefresh) {
      return {
        action: 'refresh' as const,
        reason: `Access token expira en ${Math.ceil(minutesUntilExpiry)} minutos`,
      }
    }

    return { action: 'none' as const }
  }

  // Wrapper con logging personalizado
  const baseQueryWithLogging: typeof rawBaseQuery = async (args, api, extraOptions) => {
    const result = await rawBaseQuery(args, api, extraOptions)
    if (debug) {
      // Preparar datos para logging
      const params =
        typeof args === 'string' ? '' : new URLSearchParams(args.params ?? {}).toString()
      const url = typeof args === 'string' ? args : args.url + (params ? `?${params}` : '')
      const method = typeof args === 'string' ? 'GET' : args.method ?? 'GET'
      const body = typeof args === 'string' ? null : args.body
      const status = result?.meta?.response?.status ?? result?.error?.status
      const logType = `${apiName.toUpperCase()}-API`

      // Logging detallado para métodos no GET o errores
      const restLog =
        method !== 'GET'
          ? [
              // '\nREQ:',
              // JSON.stringify(body, null, 1),
              // '\n',
              // 'RES:',
              // JSON.stringify(result.data ?? result.error, null, 1),
            ]
          : result.error
          ? ['\n', 'ERROR:', result.error]
          : [
              // '\n', 'RES:', result.data
            ]

      // Log success vs error
      if (typeof status === 'number' && status >= 200 && status < 300) {
        console.log(`[${logType}] ${status} → ${method} ${url}`, ...restLog)
      } else {
        console.error(`[${logType}] ${status} → ${method} ${url}`, ...restLog)
      }
    }
    return result
  }

  const baseQueryWithReauth: typeof rawBaseQuery = async (args, api, extraOptions) => {
    const state = api.getState() as RootState
    const { refresh_token } = state.auth.authData

    // Solo verificar tokens si no es un endpoint de autenticación
    if (!isAuthEndpoint(args) && refresh_token) {
      // Verificar estado proactivo de los tokens
      const tokenStatus = checkTokenExpiration(state.auth.authData)

      if (tokenStatus.action === 'logout') {
        api.dispatch(authActions.logout())
        return {
          error: {
            status: 'CUSTOM_ERROR',
            error: 'Refresh token expired',
          },
        }
      }

      if (tokenStatus.action === 'refresh' && !refreshPromise) {
        if (debug) {
          console.log(`[PROACTIVE-REFRESH] → ${tokenStatus.reason}`)
        }

        refreshPromise = api.dispatch(appApi.endpoints.refreshToken.initiate({ refresh_token }))

        try {
          const refreshResult = await refreshPromise
          if (!refreshResult!.data) {
            throw new Error('Proactive refresh failed')
          }
        } catch {
          api.dispatch(authActions.logout())
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Proactive refresh failed',
            },
          }
        } finally {
          refreshPromise = null
        }
      } else if (tokenStatus.action === 'refresh' && refreshPromise) {
        // Si ya hay un refresh en progreso, esperar
        try {
          await refreshPromise
          if (debug) {
            console.log(`[PROACTIVE-WAIT] → Esperando refresh en progreso`)
          }
        } catch {
          // El refresh falló
          return {
            error: {
              status: 'CUSTOM_ERROR',
              error: 'Refresh in progress failed',
            },
          }
        }
      }
    }

    let result = await baseQueryWithLogging(args, api, extraOptions)

    // Manejo reactivo de 401 (fallback si algo falló en la verificación proactiva)
    if (result.error?.status === 401 && !isAuthEndpoint(args)) {
      const state = api.getState() as RootState
      const refreshToken = state.auth.authData.refresh_token

      if (refreshToken && !refreshPromise) {
        if (debug) {
          console.log(`[REACTIVE-REFRESH] → 401 detectado, iniciando refresh reactivo`)
        }

        refreshPromise = api.dispatch(
          appApi.endpoints.refreshToken.initiate({ refresh_token: refreshToken })
        )

        try {
          const refreshResult = await refreshPromise
          if (refreshResult!.data) {
            if (debug) {
              console.log(`[REACTIVE-SUCCESS] → Token refreshed reactivamente`)
            }
            result = await baseQueryWithLogging(args, api, { ...extraOptions, isRetry: true })
          } else {
            throw new Error('Reactive refresh failed')
          }
        } catch {
          if (debug) {
            console.log(`[REACTIVE-FAILED] → Refresh reactivo falló, cerrando sesión`)
          }
          api.dispatch(authActions.logout())
        } finally {
          refreshPromise = null
        }
      } else if (refreshPromise) {
        try {
          await refreshPromise
          result = await baseQueryWithLogging(args, api, { ...extraOptions, isRetry: true })
        } catch {
          // El refresh falló, no hacer nada más
        }
      }
    }

    return result
  }

  return baseQueryWithReauth
}
